export type PrintPageKind = 'millsheet' | 'attachment'

export type PrintPagePlanMillsheet = {
  id: string
  constructionName: string
}

export type PrintPagePlanSource = {
  id: string
  millsheetId: string
  kind: PrintPageKind
  imageWidth: number | null
  imageHeight: number | null
  thumbnailFileUrls: string[]
}

export type PrintPage = {
  kind: PrintPageKind
  millsheetId: string
  s3ObjectId: string
  sourcePageIndex: number
  pageNo: number
  imageUrl: string
  isLandscape: boolean
}

export type PrintPagePlan = {
  orderedMillsheets: PrintPagePlanMillsheet[]
  pages: PrintPage[]
}

export type PrintPageTargets = {
  original: boolean
  attachments: boolean
}

type PrintPagePlanOwner = {
  id: string
  s3Objects: ReadonlyArray<{ id: string }>
}

type PrintPagePlanImage = {
  id: string
  resourceType: string
  imageWidth: number | null
  imageHeight: number | null
  thumbnailFileUrls: string[]
}

// Prisma の関連配列には順序保証がないため、紐付け側ではなく fetchPagedS3Objects の安定した取得順を正本にする。
// 同じ画像が複数ミルシートへ紐付く場合も、ミルシート入力順をタイブレーカーとして決定的に並べる。
export function buildPrintPagePlanSources({
  millsheets,
  images,
}: {
  millsheets: readonly PrintPagePlanOwner[]
  images: readonly PrintPagePlanImage[]
}): PrintPagePlanSource[] {
  return images.flatMap((image) => {
    const kind = (() => {
      if (image.resourceType === 'millsheets') {
        return 'millsheet' as const
      }
      if (image.resourceType === 'millsheetAttachments') {
        return 'attachment' as const
      }
      return null
    })()
    if (kind === null) {
      return []
    }
    return millsheets
      .filter((millsheet) => millsheet.s3Objects.some((source) => source.id === image.id))
      .map((millsheet) => ({
        id: image.id,
        millsheetId: millsheet.id,
        kind,
        imageWidth: image.imageWidth,
        imageHeight: image.imageHeight,
        thumbnailFileUrls: image.thumbnailFileUrls,
      }))
  })
}

// 番号は原本・添付の物理ページ 1 枚ごとの通し番号（1, 2, 3, …）。通常のミルシート印刷・発注照合で共通。
// 2026-10-15〜19 の間だけ「ミルシート 1 件 = 1 番号」モードがあったが、オーナー決定
// （docs/issues/20261015_03）で「Excel にはミルシートの開始ページを載せれば十分」となり、物理ページ採番に戻した。
// 集計表と原本画像で別々に採番すると、PDF欠落・添付・複数ページPDFのどこかで必ず番号がずれる。
// 実際に印刷する物理ページを先に確定し、この配列だけを採番・描画・総ページ数の正本にする。
export function buildPrintPagePlan({
  millsheets,
  sources,
  targets,
}: {
  millsheets: PrintPagePlanMillsheet[]
  sources: PrintPagePlanSource[]
  targets: PrintPageTargets
}): PrintPagePlan {
  const constructionNames = Array.from(new Set(millsheets.map((millsheet) => millsheet.constructionName)))
  const orderedMillsheets = constructionNames.flatMap((constructionName) =>
    millsheets.filter((millsheet) => millsheet.constructionName === constructionName),
  )
  const enabledKinds: PrintPageKind[] = [
    ...(targets.original ? (['millsheet'] as const) : []),
    ...(targets.attachments ? (['attachment'] as const) : []),
  ]
  const pagesWithoutNumbers = orderedMillsheets.flatMap((millsheet) =>
    enabledKinds.flatMap((kind) =>
      sources
        .filter(
          (source) =>
            source.millsheetId === millsheet.id &&
            source.kind === kind &&
            source.imageWidth !== null &&
            source.imageHeight !== null,
        )
        .flatMap((source) =>
          source.thumbnailFileUrls.map((imageUrl, sourcePageIndex) => ({
            kind,
            millsheetId: millsheet.id,
            s3ObjectId: source.id,
            sourcePageIndex,
            imageUrl,
            isLandscape: (source.imageWidth ?? 0) > (source.imageHeight ?? 0),
          })),
        ),
    ),
  )

  return {
    orderedMillsheets,
    pages: pagesWithoutNumbers.map((page, index) => ({ ...page, pageNo: index + 1 })),
  }
}

export function findFirstMillsheetPageNo(pages: PrintPage[], millsheetId: string): number | null {
  return pages.find((page) => page.millsheetId === millsheetId && page.kind === 'millsheet')?.pageNo ?? null
}

// ミルシートの「開始ページ」（そのミルシートで最初に印刷される物理ページの番号）。ホームの Excel / CSV のページ番号列に使う。
// 1 ミルシートに複数ページ・複数 PDF があると次のミルシートの番号は飛ぶが、Excel には開始ページが載っていれば原本を
// 探せるため十分（2026-10-19 オーナー決定。顧客も了承済み）。
// 原本より添付が先に印刷されることはない（page plan が原本 → 添付の順に並べる）ので、添付だけが印刷される
// ミルシートではその添付の番号になり、印字される番号と Excel の番号が一致する
export function findMillsheetStartPageNo(pages: PrintPage[], millsheetId: string): number | null {
  return pages.find((page) => page.millsheetId === millsheetId)?.pageNo ?? null
}

export function buildSummaryPageLabels({
  pageNo,
  itemCount,
  hasPrintableOriginal,
  originalSelected,
}: {
  pageNo: number | null
  itemCount: number
  hasPrintableOriginal: boolean
  originalSelected: boolean
}): string[] {
  const label = (() => {
    if (pageNo !== null) {
      return `${pageNo}`
    }
    if (!hasPrintableOriginal) {
      return 'PDFなし'
    }
    if (!originalSelected) {
      return '印刷対象外'
    }
    return 'PDFなし'
  })()
  const effectiveItemCount = Math.max(itemCount, 1)
  if (pageNo === null || effectiveItemCount <= 1) {
    return Array.from({ length: effectiveItemCount }, () => label)
  }
  return Array.from({ length: effectiveItemCount }, (_, index) => `${pageNo}-${index + 1}`)
}

export function hasPrintableMillsheetPage(sources: PrintPagePlanSource[], millsheetId: string): boolean {
  return sources.some(
    (source) =>
      source.millsheetId === millsheetId &&
      source.kind === 'millsheet' &&
      source.imageWidth !== null &&
      source.imageHeight !== null &&
      source.thumbnailFileUrls.length > 0,
  )
}
